/**
 * A confirmation for a consequential operator action (activate on air,
 * deactivate, reset, stop a layer, switch over — `lib/playout/safety.ts`
 * says which and how firmly): what will happen, one confirm button in the
 * action's colour, and cancel. The action itself is the container's
 * (`onConfirm`); while it is pending the dialog stays open and says so.
 */
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';

export type ConfirmDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  /** `take`: puts something on air; `destructive`: stops or discards something. */
  readonly tone: 'take' | 'destructive' | 'default';
  readonly pending: boolean;
  /**
   * The action cuts live air (`lib/playout/safety.ts`): Cancel has the focus, so a reflexive Enter — the key that
   * also takes — cancels. Otherwise the confirm button has it.
   */
  readonly guarded: boolean;
  readonly onConfirm: () => void;
};

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, tone, pending, guarded, onConfirm }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirm-dialog" data-guarded={guarded}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending} autoFocus={guarded} data-testid="confirm-cancel">
            Cancel
          </Button>
          <Button type="button" variant={tone} onClick={onConfirm} disabled={pending} data-testid="confirm-action" autoFocus={!guarded}>
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
